import { useState } from 'react';
import { Camera, X } from 'lucide-react';

const IMAGES = {
  hero: "/images/hero-children.jpeg",
  founder: "/images/founder-brianna.jpeg",
  event1: "/images/children-hugging.jpeg",
  event2: "/images/event-stage.jpeg",
};

const galleryImages = [
  {
    src: IMAGES.hero,
    alt: "Children celebrating at Fun in the Sun event",
    caption: "Joy and celebration at Fun in the Sun"
  },
  {
    src: IMAGES.founder,
    alt: "Brianna Thomas, Founder of Protopia",
    caption: "Our founder Brianna Thomas"
  },
  {
    src: IMAGES.event1,
    alt: "Two children hugging at the event",
    caption: "Building friendships and community"
  },
  {
    src: IMAGES.event2,
    alt: "Fun in the Sun event stage",
    caption: "The stage where dreams come alive"
  }
];

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="gallery-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF7A59] rounded-full mb-6">
              <Camera className="w-8 h-8 text-white" />
            </div>
            <span className="font-accent text-2xl text-[#FF7A59]">Our Moments</span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
              Gallery
            </h1>
            <p className="text-xl text-[#64748B] leading-relaxed">
              Capturing the joy, laughter, and transformative moments from our Fun in the Sun events. 
              Every photo tells a story of hope and community.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white" data-testid="gallery-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {galleryImages.map((image, index) => (
              <div 
                key={index}
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${index}`}
                className="group relative rounded-3xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-all duration-300"
              >
                <img 
                  src={image.src} 
                  alt={image.alt}
                  className="w-full h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-6 left-6 right-6">
                    <p className="text-white font-semibold text-lg">{image.caption}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
            onClick={() => setSelectedImage(null)}
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="max-w-5xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="max-w-full max-h-[80vh] object-contain rounded-2xl"
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImage.caption}</p>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#FDFBF7]" data-testid="gallery-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
            Be Part of These Moments
          </h2>
          <p className="text-lg text-[#64748B] mb-8">
            Help us create more memories and transform more lives. Your support makes events like these possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/donate"
              data-testid="gallery-donate-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
            >
              Make a Donation
            </a>
            <a 
              href="/volunteer"
              data-testid="gallery-volunteer-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#E2E8F0] bg-white text-[#1E293B] font-bold px-8 py-4 hover:bg-[#F3F4F6] transition-all duration-200"
            >
              Volunteer With Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;
